import React from 'react';
import { X, Zap } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface OsmSwitchyardProperties {
  id: number;
  osm_type: string;
  osm_id: string | number;
  name: string | null;
  operator: string | null;
  voltage_kv: number | null;
  classifier: 'switchgear' | 'name_regex' | 'transmission' | null;
  state: string | null;
  hifld_backed: boolean | null;
}

interface Props {
  switchyard: OsmSwitchyardProperties;
  onClose: () => void;
}

const CLASSIFIER_LABELS: Record<string, { label: string; desc: string }> = {
  switchgear: {
    label: 'power=switchgear',
    desc: 'OSM tag explicitly flags this as switchgear infrastructure. Rare but definitive.',
  },
  name_regex: {
    label: 'Transmission substation, switchyard name',
    desc: 'Tagged as substation=transmission; OSM mapper gave it a name matching "switchyard/switching station/switch sta/switchgear". Highest attribute completeness (~99% voltage/operator).',
  },
  transmission: {
    label: 'substation=switching',
    desc: 'OSM canonical tag for a pure switching station (no transformation, just circuit routing).',
  },
};

export const OsmSwitchyardDetail: React.FC<Props> = ({ switchyard, onClose }) => {
  const { theme } = useTheme();
  const s = switchyard;
  const clsInfo = s.classifier ? CLASSIFIER_LABELS[s.classifier] : null;
  const accent = s.hifld_backed === false ? '#10b981' : '#f59e0b';
  const osmUrl = `https://www.openstreetmap.org/${s.osm_type}/${s.osm_id}`;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {s.name || '(unnamed switchyard)'}
          </h2>
          <div style={{
            marginTop: '4px', display: 'inline-block',
            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
            background: accent + '20', color: accent, borderRadius: '4px',
          }}>
            <Zap size={10} style={{ marginRight: 4, verticalAlign: -1 }} />
            {s.hifld_backed === false ? 'HIFLD-MISSING' : 'HIFLD-CONFIRMED'}
            {s.voltage_kv != null ? ` · ${Number(s.voltage_kv).toLocaleString()} kV` : ''}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, padding: 4 }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        <Section title="Identification">
          <Field label="Name" value={s.name || '—'} />
          <Field label="Operator" value={s.operator || '—'} />
          <Field label="Voltage" value={s.voltage_kv != null ? `${Number(s.voltage_kv).toLocaleString()} kV` : '—'} />
          <Field label="State" value={s.state || '—'} />
        </Section>

        <Section title="OSM provenance">
          <Field label="OSM feature" value={`${s.osm_type} / ${s.osm_id}`} />
          {clsInfo && <Field label="Capture rule" value={clsInfo.label} />}
          {clsInfo && (
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: theme.textMuted, lineHeight: 1.5 }}>
              {clsInfo.desc}
            </p>
          )}
          <p style={{ margin: '12px 0 0', fontSize: '12px' }}>
            <a href={osmUrl} target="_blank" rel="noopener noreferrer"
               style={{ color: accent, textDecoration: 'underline' }}>
              View on OpenStreetMap ↗
            </a>
          </p>
        </Section>

        <Section title="HIFLD cross-reference">
          {s.hifld_backed === false ? (
            <p style={{ margin: 0, fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
              <strong style={{ color: '#10b981' }}>Net-new visibility.</strong> HIFLD does not carry a
              substation within 200m of this point. This facility is absent from standard commercial
              datasets that rely on HIFLD. Field-verify before any LOI — OSM data is volunteer-maintained
              and may be stale, but a named 345 kV switching station at this location is a strong
              indication of real transmission access.
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
              Matched to a HIFLD substation within 200m. OSM provides a richer name/voltage/operator
              attribution than HIFLD often carries (HIFLD frequently uses truncated placeholders like
              "UNKNOWN#####" or "TAP#####" for the same facility).
            </p>
          )}
        </Section>
      </div>
    </div>
  );
};
